// botIdentity.js — S2-B phase 1: the SPA's selectedBot comes from the DB record for EVERY id
// shape (2026-09-22). Design: porting/design-notes/S2_IDENTITY_DESIGN.md §3.1 (the builder table
// is the spec) + §3.2 (host id) + decision 1 = A (hard fail).
//
// ⛔ WHAT THIS REPLACES. Both index pages split on `isRagBot = !id.includes('asst_')`: asst_ ids
// took their selectedBot VERBATIM from the compiled src/data/iBot.json, everything else built one
// from the API — and the API builder dropped `MemoryRecall` and `footer`. The voice socket then
// opened as the hardcoded fallback constant (Nova) for every non-asst_ id, so an agent_* link loaded ITS
// config but spoke as Nova. One builder, one host-id rule, no compiled roster, no Nova.
//
// SOURCES (post-token; /agent-bootstrap is the PRE-auth surface and lacks name/model/voice/tools/
// MemoryRecall/settings — it is not a source here):
//   assistantData  GET /assistants/{agentId}          — session fields (model, voice, tools, name)
//   settingsData   GET /botsettings/{id}              — the Wave-2 record {agentId, legacyAssistantId,
//                  (getBotSettings; botapi resolves    orgId, runtime, config (was iBot.json),
//                   asst_/agent_/ObjectId server-side) branding (was settings.json)}
//   configData     GET /assistants/{agentId}/config   — the pre-Wave-2 subresource, fallback only
//
// ⚠️ AVAILABILITY = HARD FAIL (decision A). No JSON fallback, no snapshot, no partial bot: without
// an assistants record there is no bot, and the page renders BOT_UNAVAILABLE instead.
//
// ⚠️ PLAIN JS ON PURPOSE (same reason as guestGate.js): imported by the TSX pages AND by
// `node --test relay-server/test/`, so the test runs the real code.

export const DEFAULT_REALTIME_ENDPOINT = 'wss://api.openai.com/v1/realtime';

/** The one user-facing text of the unified failure state (both pages). */
export const BOT_UNAVAILABLE_TEXT =
  '⛔ This assistant is unavailable right now. Please try again later.';

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const nonEmptyStr = (v) => typeof v === 'string' && v.trim() !== '';
/** First value that is present: a non-empty string, a boolean, a number, an array, or an object. */
const first = (...vals) => {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    return v;
  }
  return undefined;
};
/** Nested section of a BotSettings record; `{}` for the flat pre-Wave-2 shape (never the record itself). */
const section = (record, name) => (isObj(record) && isObj(record[name]) ? record[name] : {});

/**
 * Host id for the voice socket and session.update (§3.2):
 *   hostId = record.legacyAssistantId || record.id
 * asst_ while the Assistants API lives; a native agent_ (no legacyAssistantId) sends its own id and
 * the relay resolves it from the DB. NEVER a fallback bot — empty means the caller fails.
 */
export function resolveHostId(assistantData, settingsData) {
  const legacy = first(
    nonEmptyStr(settingsData?.legacyAssistantId) ? settingsData.legacyAssistantId : undefined,
    nonEmptyStr(assistantData?.legacyAssistantId) ? assistantData.legacyAssistantId : undefined,
  );
  const recordId = first(
    nonEmptyStr(settingsData?.agentId) ? settingsData.agentId : undefined,
    nonEmptyStr(assistantData?.id) ? assistantData.id : undefined,
  );
  return (legacy || recordId || '').trim();
}

/**
 * Build selectedBot (iBot.json shape — every downstream reader keys on it) from the DB record.
 *
 * @param {object|null} assistantData  assistants record (required — no record, no bot)
 * @param {object|null} settingsData   BotSettings record (nested runtime/config/branding)
 * @param {object|null} configData     legacy /config subresource (fallback)
 * @param {{requestedId?: string}} [opts]  the ?id= the page is keyed on. It stays the bot's
 *   `id`/`botid` so every existing `bot.botid === assistantId` lookup, language cookie and
 *   MemoryRecall cache key keeps matching — the file path had exactly that property.
 * @returns {{ok:true, bot:object, hostId:string} | {ok:false, reason:string}}
 */
export function buildSelectedBotFromRecord(assistantData, settingsData, configData, opts = {}) {
  const a = isObj(assistantData) ? assistantData : {};
  const s = isObj(settingsData) ? settingsData : {};
  const c = isObj(configData) ? configData : {};
  const cfg = section(s, 'config');
  const brand = section(s, 'branding');

  if (!nonEmptyStr(a.id)) {
    return { ok: false, reason: 'no assistants record (GET /assistants/{id} returned nothing usable)' };
  }
  const hostId = resolveHostId(a, s);
  if (!hostId) return { ok: false, reason: 'empty host id (record has neither legacyAssistantId nor id)' };

  const requestedId = nonEmptyStr(opts.requestedId) ? opts.requestedId.trim() : '';
  const mediaSrc = first(cfg.media, c.media, a.media) || {};
  const bot = {
    // FAITHFUL PASS-THROUGH first: the file path handed downstream the iBot.json entry VERBATIM, and
    // Wave 2 copied every field it did not route elsewhere into BotSettings.config. ChatUI reads
    // keys no table row names — `ontology` (Ask Joe), `promptbuttons` (Coach Dan, Charlotte),
    // `canonicalMedia` (Ask Steve), `integration.EmailConfig`, `mediaKeys` — so an explicit-field-
    // only builder would silently drop them for asst_ bots. BotSettings.config wins over /config.
    ...c,
    ...cfg,
    id: requestedId || first(s.agentId, a.id, c.id),
    botid: requestedId || first(a.botid, a.botId, c.botid, s.agentId, a.id),
    // `name`: BotSettings is current, the assistants name is the OpenAI Assistant object's, frozen
    // at migration (B1b, GB ruling 2026-09-22: "Ask Joe", not "Trader Joe's"). The relay reads
    // `runtime.botName` (bot-config.js normalizeBotSettingsRecord); config.name is the SPA's own.
    name: first(cfg.name, s.runtime?.botName, a.name, c.name) || 'AI Assistant',
    // session fields — the assistants record is authoritative (Wave 2 dropped them from config;
    // config.model/voice/tools are null on all 18 live records by design)
    model: first(a.model, c.model, cfg.model) || 'gpt-realtime-mini',
    voice: first(a.voice, c.voice, cfg.voice) || 'alloy',
    tools: first(a.tools, c.tools, cfg.tools) || [],
    // config fields (was iBot.json) — BotSettings.config, then the legacy subresource
    orgs: first(cfg.orgs, c.orgs, a.orgs) || [],
    endpoint: first(cfg.endpoint, c.endpoint, a.endpoint) || DEFAULT_REALTIME_ENDPOINT,
    dns: first(cfg.dns, c.dns, a.dns) || [],
    type: first(cfg.type, c.type, a.type) || 'voice,text',
    welcome: first(cfg.welcome, c.welcome, a.welcome) || '',
    title: first(cfg.title, c.title, a.title, a.name) || 'AI Assistant',
    // persona/bottype: config first, like every other iBot.json field (B1b audit — no live record
    // carries them on assistants, so this only closes the latent inversion)
    persona: first(cfg.persona, c.persona, a.persona) || '',
    bottype: first(cfg.bottype, c.bottype, a.bottype) || '',
    header: first(cfg.header, c.header) || { link: '/' },
    presets: first(cfg.presets, c.presets, a.presets) || {},
    iconSet: first(cfg.iconSet, c.iconSet, a.iconSet) || {},
    media: mediaSrc,
    prompts: first(cfg.prompts, c.prompts, a.prompts) || [],
    // `settings` is a SIBLING of media; Ask Rex / Ask Lexi once nested it inside media (the
    // migration hoists it — botsettings-migration.js MEDIA_RESERVED_KEYS). Read both.
    settings: first(cfg.settings, c.settings, isObj(mediaSrc.settings) ? mediaSrc.settings : undefined)
      || { showThumb: true, showAvatar: true },
    // branding-routed fields (Wave 2 moved these four out of config: IBOT_TO_BRANDING)
    theme: first(brand.theme, cfg.theme, c.theme, a.theme) || {},
    thumbnail: first(brand.thumbnail, cfg.thumbnail, c.thumbnail, a.thumbnail) || '',
    avatar: first(brand.avatar, cfg.avatar, c.avatar, a.avatar) || '',
    // ✚ the two rows the old API builder dropped (§3.1)
    MemoryRecall: first(cfg.MemoryRecall, c.MemoryRecall, a.MemoryRecall) === true,
  };
  const footer = first(brand.footer, cfg.footer, c.footer, a.footer);
  if (isObj(footer)) bot.footer = footer;
  return { ok: true, bot, hostId };
}

/**
 * Branding keys the browser may hold — the settings.json vocabulary Header/ChatUI/seoUtils read,
 * plus the four Wave 2 routed into branding. An ALLOWLIST, applied to every source.
 *
 * ⛔ B1c (2026-09-23). The first version stored `legacySettings` whole when BotSettings branding
 * read empty. That source — GET /assistants/{agentId}/setting — returns the ENTIRE BotSettings
 * document (id, agentId, legacyAssistantId, orgId, runtime, config, branding, schema, enabled),
 * and it answers WITHOUT a token, while GET /botsettings/{id} 401s without one. So on any /ibot
 * load with no session, `runtime.credentialProfile` landed in localStorage. Forbidden twice over:
 * BOTSETTINGS_DB_SCHEMA_SPEC.md §2a (a browser-facing projection never carries `runtime`,
 * integration/email config or raw JSON) and openaiAPI.tsx:11-14 / 79856a5 (a browser must not
 * carry even the NAME of a key). Hence: branding sub-object only, projected through this list;
 * `integration` is deliberately absent (EmailConfig/Stripe/MCP/GA — ChatUI reads it from
 * selectedBot.integration instead).
 */
export const BRANDING_KEYS = Object.freeze([
  'name', 'title', 'subtitle', 'description', 'applicationCategory', 'inLanguage', 'softwareVersion',
  'homepage', 'url', 'favicon', 'logo', 'edition', 'bodyStyle', 'buttonStyle', 'chatStyle',
  'headerBackColor', 'promptButtonColor', 'fontFace', 'containerStyle', 'conversationStyle',
  'footer', 'flyout', 'creator', 'offers', 'type', 'hamburgerPic', 'themeColors',
  'theme', 'avatar', 'thumbnail',
]);

/** [S2-B B2] Allowlist-project a branding object (exported for the pre-auth surface, agentBootstrap.js). */
export const projectBranding = (src) => {
  const out = {};
  for (const k of BRANDING_KEYS) if (src[k] !== undefined && src[k] !== null) out[k] = src[k];
  return out;
};

/**
 * The `botSettings` payload Header/ChatUI read (settings.json shape): the record's `branding`
 * sub-object, allowlisted. Sources, in order: BotSettings (GET /botsettings/{id}), then the legacy
 * /setting response — which is the same document, so only ITS `branding` sub-object is read.
 * A source without a nested `branding` object is NOT branding and is never used.
 * @returns {{payload: object|null, source: 'botsettings'|'legacy-setting'|null}}
 *   payload null ⇒ the caller stores NOTHING (and says so on the console).
 */
export function buildBotSettingsPayload(bot, settingsData, legacySettings) {
  if (!bot) return { payload: null, source: null };
  const candidates = [['botsettings', settingsData], ['legacy-setting', legacySettings]];
  for (const [source, record] of candidates) {
    const brand = projectBranding(section(record, 'branding'));
    if (!Object.keys(brand).length) continue;
    return {
      source,
      payload: {
        ...brand,
        botid: bot.botid,
        name: brand.name || bot.title || bot.name,
        logo: brand.logo || bot.thumbnail || bot.avatar || '',
      },
    };
  }
  return { payload: null, source: null };
}

/** The unified failure state: items for the chat pane, plus the console line naming the reason. */
export function botUnavailableState(requestedId, reason) {
  return {
    items: [{ role: 'system', formatted: { text: BOT_UNAVAILABLE_TEXT } }],
    log: `[BotIdentity] bot unavailable: id=${requestedId || '(none)'} — ${reason}`,
  };
}

/** Window event Header listens for: the record branding was just stored (detail: { botid }). */
export const BOT_SETTINGS_STORED_EVENT = 'botSettingsStored';

/**
 * The ONE writer of the record-branding `botSettings` payload (both pages' record fetch). Writes
 * localStorage and the cookie exactly as before, THEN tells Header to re-read it on this same load:
 * Header mounts before the fetch resolves, and on a repeat visit nothing else it depends on changes.
 * A write that throws never dispatches, so a listener can never read a payload that is not there.
 * `env` is injectable for tests; the browser globals are the default.
 */
export function storeBotSettings(payload, env = {}) {
  const storage = env.storage || globalThis.localStorage;
  const doc = env.document || globalThis.document;
  const win = env.window || globalThis.window;
  const json = JSON.stringify(payload);
  storage.setItem('botSettings', json);
  doc.cookie = `botSettings=${encodeURIComponent(json)}; path=/`;
  win.dispatchEvent(new CustomEvent(BOT_SETTINGS_STORED_EVENT, { detail: { botid: payload?.botid } }));
}
